// AWS account connection (#420): what a workspace starts, what the stack reports,
// and what the platform verified.
//
// Read off `routes/connections.py`. The launch link carries the external id
// as a `NoEcho` parameter; it appears nowhere else in any answer, and the
// template itself is served at `TEMPLATE_PATH` for a launch by hand when the
// estate has not published its bucket yet (`launch_available` says which).

import type { Api } from "./http";

export const ACCOUNTS_PATH = "/api/v1/cloud-accounts";
export const CONNECTIONS_PATH = `${ACCOUNTS_PATH}/connections`;
export const TEMPLATE_PATH = `${ACCOUNTS_PATH}/template`;

export type ConnectionState = "awaiting_stack" | "verifying" | "connected" | "failed";

export interface Connection {
  id: string;
  state: ConnectionState;
  /** What the workspace calls the account (#823), carried onto it once verified. */
  name: string | null;
  regions: string[];
  account_id: string | null;
  role_arn: string | null;
  template_version: string | null;
  callback_received_at: string | null;
  verified_at: string | null;
  error: string | null;
  /**
   * When a connected connection's last check was refused (#1118), `error` saying why; null
   * where its last check passed, and on every connection that is not connected. The account
   * stays connected meanwhile.
   */
  last_check_failed_at?: string | null;
  cloud_account_id: string | null;
  created_at: string;
  updated_at: string;
  stack_name: string;
  /** The stack's Events tab in the CloudFormation console, in the region the launch uses (#825). */
  stack_events_url: string;
  /** The alert source this connection's launch made, where it made one (#860); null otherwise. */
  created_alert_source_id?: string | null;
}

/**
 * Days a connection may stay unconnected after it started before it expires: the server's
 * `PENDING_LIFETIME` (#825), which no read carries, pinned to it by a test (#953).
 */
export const PENDING_LIFETIME_DAYS = 7;

export interface Launch {
  template_url: string | null;
  launch_url: string | null;
  stack_name: string;
  parameters: Record<string, string>;
}

export interface ConnectedAccount {
  id: string;
  account_id: string;
  /** Null until someone names it: the screens show the id instead (#823). */
  name: string | null;
  role_arn: string;
  /** The operator role, where the account's stack made one (#262); null where it made none. */
  operator_role_arn?: string | null;
  regions: string[];
  status: string;
  connected_at: string;
}

export interface AccountsRead {
  connections: Connection[];
  accounts: ConnectedAccount[];
  launch_available: boolean;
}

export type Started = Connection & { launch: Launch };

/** The regions a workspace may choose from. eu-west-1 first: the estate's own. */
export const REGIONS: ReadonlyArray<[string, string]> = [
  ["eu-west-1", "Europe (Ireland)"],
  ["eu-west-2", "Europe (London)"],
  ["eu-central-1", "Europe (Frankfurt)"],
  ["eu-north-1", "Europe (Stockholm)"],
  ["us-east-1", "US East (N. Virginia)"],
  ["us-east-2", "US East (Ohio)"],
  ["us-west-2", "US West (Oregon)"],
  ["ap-southeast-1", "Asia Pacific (Singapore)"],
  ["ap-southeast-2", "Asia Pacific (Sydney)"],
];

export const readAccounts = (api: Api): Promise<AccountsRead> => api.get<AccountsRead>(ACCOUNTS_PATH);

export const startConnection = (api: Api, regions: string[], name: string | null = null): Promise<Started> =>
  api.post<Started>(CONNECTIONS_PATH, name === null ? { regions } : { regions, name });

/** Name a connected account, or clear its name with null (#823). */
export const renameAccount = (api: Api, cloudAccountId: string, name: string | null): Promise<ConnectedAccount> =>
  api.patch<ConnectedAccount>(`${ACCOUNTS_PATH}/${encodeURIComponent(cloudAccountId)}`, { name });

/**
 * Disconnect a connected account (#828): the server starts no new investigation or scan on
 * it and stops trusting its topics, and answers the account as it now reads. It issues no
 * more credentials for the account either, which is what stops work already in flight (#1093).
 */
export const disconnectAccount = (api: Api, cloudAccountId: string): Promise<ConnectedAccount> =>
  api.post<ConnectedAccount>(`${ACCOUNTS_PATH}/${encodeURIComponent(cloudAccountId)}/disconnect`);

/** The longest name the server keeps: AWS's own limit (`connections.ACCOUNT_NAME_MAX_CHARS`). */
export const ACCOUNT_NAME_MAX_CHARS = 50;

/** What a name may hold, as AWS's own account names may (#823), said once for both fields. */
export const ACCOUNT_NAME_RULE =
  "Up to 50 characters: letters A–Z, digits, spaces and punctuation other than < and >.";

export const relaunch = (api: Api, connectionId: string): Promise<Started> =>
  api.get<Started>(`${CONNECTIONS_PATH}/${encodeURIComponent(connectionId)}/launch`);

export const checkConnection = (
  api: Api,
  connectionId: string,
  accountId: string | null,
): Promise<Connection> =>
  api.post<Connection>(
    `${CONNECTIONS_PATH}/${encodeURIComponent(connectionId)}/check`,
    accountId === null ? {} : { account_id: accountId },
  );

/**
 * Cancel a connection that is not connected yet (#825): the server deletes it, and refuses
 * its stack's callback if one ever comes.
 */
export const cancelConnection = (api: Api, connectionId: string): Promise<void> =>
  api.delete<void>(`${CONNECTIONS_PATH}/${encodeURIComponent(connectionId)}`);

/** What a person reads for each state. */
export const STATE_TEXT: Record<ConnectionState, { label: string; tone?: "good" | "bad" | "warn" }> = {
  awaiting_stack: { label: "Waiting for the stack", tone: "warn" },
  verifying: { label: "Verifying", tone: "warn" },
  connected: { label: "Connected", tone: "good" },
  failed: { label: "Not connected yet", tone: "bad" },
};
